import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@design-system';
import { BuildEnvironment, builtEnvironment, type FeatureFlagDiff } from '@shared';
import { PendingChangesCart } from './PendingChangesCart';
import { usePublishFeatureFlagDraft } from '../hooks/use-publish-feature-flag-draft';

type PublishDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diff: FeatureFlagDiff;
  basedOnHash: string | undefined;
  onPublished?: () => void | Promise<unknown>;
};

type Stage = 'review' | 'confirm';

const PROD_CONFIRM_PHRASE = 'PUBLISH';

export function PublishDialog({
  open,
  onOpenChange,
  diff,
  basedOnHash,
  onPublished,
}: PublishDialogProps) {
  const [stage, setStage] = useState<Stage>('review');
  const [typedConfirm, setTypedConfirm] = useState('');

  const isProd = builtEnvironment === BuildEnvironment.prod;
  const mutation = usePublishFeatureFlagDraft();

  const prodReady = useMemo(
    () => (isProd ? typedConfirm.trim() === PROD_CONFIRM_PHRASE : true),
    [isProd, typedConfirm],
  );

  const resetAndClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStage('review');
      setTypedConfirm('');
      mutation.reset();
    }
    onOpenChange(nextOpen);
  };

  const onPublish = async () => {
    if (!basedOnHash) return;
    try {
      await mutation.mutateAsync({ basedOnHash });
      await onPublished?.();
      resetAndClose(false);
    } catch {
      // Error state is surfaced inline via mutation.error below.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={resetAndClose}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {stage === 'review' ? 'Review pending changes' : 'Confirm publish'}
          </DialogTitle>
          <DialogDescription>
            {stage === 'review'
              ? 'These changes will be applied to live AWS AppConfig when you publish.'
              : `You're about to deploy ${diff.entries.length} change${diff.entries.length === 1 ? '' : 's'} to the ${builtEnvironment} environment.`}
          </DialogDescription>
        </DialogHeader>

        {stage === 'review' && (
          <div className="max-h-96 overflow-auto">
            <PendingChangesCart
              diff={diff}
              operations={[]}
              isSaving={false}
              isBehindLatest={false}
              onRemoveOp={() => {}}
              onClearAll={() => {}}
            />
          </div>
        )}

        {stage === 'confirm' && isProd && (
          <div className="space-y-2">
            <Label htmlFor="prod-confirm-input">
              Type <code className="rounded bg-gray-100 px-1">{PROD_CONFIRM_PHRASE}</code>{' '}
              to confirm
            </Label>
            <Input
              id="prod-confirm-input"
              autoFocus
              value={typedConfirm}
              onChange={e => setTypedConfirm(e.target.value)}
              placeholder={PROD_CONFIRM_PHRASE}
            />
          </div>
        )}

        {mutation.isError && (
          <p className="text-sm text-red-700">
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Publish failed.'}
          </p>
        )}

        <DialogFooter>
          {stage === 'review' && (
            <>
              <Button
                variant="outline"
                onClick={() => resetAndClose(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary hover:bg-primary-hover"
                onClick={() => setStage('confirm')}
                disabled={!diff.hasChanges || !basedOnHash}
              >
                Continue
              </Button>
            </>
          )}
          {stage === 'confirm' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStage('review')}
                disabled={mutation.isPending}
              >
                Back
              </Button>
              <Button
                className="bg-primary hover:bg-primary-hover"
                onClick={onPublish}
                disabled={!prodReady || mutation.isPending || !basedOnHash}
              >
                {mutation.isPending ? 'Publishing…' : 'Publish to AppConfig'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
